export const API_BASE = 'https://www.math-teaching.org/api/';


async function handleResponse(res) {
  const text = await res.text();
  if (!res.ok) {
    let message = text;
    try {
      const data = JSON.parse(text);
      message = data.error || message;
    } catch (e) {
      // Fall back to raw text.
    }
    throw new Error(message || 'Request failed');
  }
  try {
    return JSON.parse(text);
  } catch (e) {
    return text;
  }
}

export async function getPracticeExam(grade) {
  return handleResponse(await fetch(`${API_BASE}/api/practice-exams/${grade}`));
}

export async function submitPracticeExam(grade, answers) {
  return handleResponse(await fetch(`${API_BASE}/api/practice-exams/${grade}/submit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ answers }),
  }));
}

export async function uploadPdf({ file }) {
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch(`${API_BASE}/api/upload`, {
    method: 'POST',
    body: formData,
  });
  return handleResponse(res);
}

export async function login(email, password) {
  const res = await fetch(`${API_BASE}/api/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  return handleResponse(res);
}

export async function getLoginCount() {
  const res = await fetch(`${API_BASE}/api/auth/login-count`);
  return handleResponse(res);
}

export async function signup(email, password) {
  const res = await fetch(`${API_BASE}/api/auth/signup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  return handleResponse(res);
}

export async function createPracticeSession(email, password) {
  const res = await fetch(`${API_BASE}/api/sessions/practice`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  return handleResponse(res);
}

export async function generateSimilarPractice(email, password, sessionId, questionId) {
  const res = await fetch(`${API_BASE}/api/generate-similar-practice`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, sessionId, questionId }),
  });
  return handleResponse(res);
}

export async function createPracticeSessionFromStandard(email, password, selector) {
  const res = await fetch(`${API_BASE}/api/sessions/practice-from-standard`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, ...selector }),
  });
  return handleResponse(res);
}

export async function createPracticeSessionFromQuestions(email, password, questions) {
  const res = await fetch(`${API_BASE}/api/sessions/practice-from-questions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, questions }),
  });
  return handleResponse(res);
}

export async function saveQuestion(sessionId, payload) {
  const res = await fetch(`${API_BASE}/api/questions/${sessionId}/save`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return handleResponse(res);
}

export async function uploadAnswerSheet(sessionId, file) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('sessionId', sessionId);
  const res = await fetch(`${API_BASE}/api/upload-answer-sheet`, {
    method: 'POST',
    body: formData,
  });
  return handleResponse(res);
}

export async function gradeAnswerSheet(sessionId, answerSheetIndex = -1) {
  const res = await fetch(`${API_BASE}/api/grade-answer-sheet`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sessionId, answerSheetIndex }),
  });
  return handleResponse(res);
}

export async function gradeAnswers(sessionId, answers) {
  const res = await fetch(`${API_BASE}/api/grade-answers`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sessionId, answers }),
  });
  return handleResponse(res);
}

export async function saveProgress(email, password, sessionId, progress) {
  const res = await fetch(`${API_BASE}/api/progress/save`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, sessionId, progress }),
  });
  const data = await handleResponse(res);
  clearProgressSummaryCache();
  return data;
}

export async function getProgress(email, password) {
  const res = await fetch(`${API_BASE}/api/progress`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  return handleResponse(res);
}

const SUMMARY_CACHE_MS = 15000;
const progressSummaryCache = new Map();

export function clearProgressSummaryCache() {
  progressSummaryCache.clear();
}

export async function getProgressSummary(email, password, { state, gradeLevel } = {}) {
  const cacheKey = `${email}|${state || ''}|${gradeLevel || ''}`;
  const cached = progressSummaryCache.get(cacheKey);
  if (cached && Date.now() - cached.at < SUMMARY_CACHE_MS) {
    return cached.data;
  }

  const res = await fetch(`${API_BASE}/api/progress/summary`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, state, gradeLevel }),
  });
  const data = await handleResponse(res);
  progressSummaryCache.set(cacheKey, { at: Date.now(), data });
  return data;
}

export async function getCatalog() {
  const res = await fetch(`${API_BASE}/api/catalog`);
  return handleResponse(res);
}

const CATALOG_STORAGE_KEY = 'mathTutor.catalogStructure';
let catalogStructurePromise = null;

function readStoredCatalog() {
  try {
    const raw = window.sessionStorage.getItem(CATALOG_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

function storeCatalog(data) {
  try {
    window.sessionStorage.setItem(CATALOG_STORAGE_KEY, JSON.stringify(data));
  } catch (e) {
    // Quota / private mode — the in-memory promise still covers this tab.
  }
}

// The topic tree changes only when the question CSVs do. Browse pages share
// one in-flight request, and a sessionStorage copy makes reloads instant.
export function getCatalogStructure() {
  if (!catalogStructurePromise) {
    const stored = readStoredCatalog();
    const network = fetch(`${API_BASE}/api/catalog/structure`)
      .then(handleResponse)
      .then((data) => {
        storeCatalog(data);
        catalogStructurePromise = Promise.resolve(data);
        return data;
      })
      .catch((err) => {
        if (!stored) catalogStructurePromise = null;
        throw err;
      });
    catalogStructurePromise = stored ? Promise.resolve(stored) : network;
  }
  return catalogStructurePromise;
}
